import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import {
  autoMatchColumns,
  fetchMapping,
  fetchMetadata,
  updateMapping,
} from '@src/api/actions/dashboard/dataIntegrations';

import {
  StepColumnMapping,
  buildTransformationsFromRules,
  rulesFromTransformations,
} from './DataIntegrationWizardPage';


// Remap dialog for an existing mapping. Lets a client:
//   1. Review and adjust how each CSV column maps to a backend field.
//   2. Upload a fresh sample file if their CRM renamed / added columns, so
//      we detect the new headers and merge with the mappings they already
//      have where the header name is unchanged.
//   3. Adjust the demo / issue / date transform rules.
//
// Saves via PATCH on the per-type CRUD endpoint — only updates
// column_mappings + transform_params, leaves credentials + schedule alone.
// Manual mappings support this too (PATCH must be enabled on that viewset).
export default function DataIntegrationRemapDialog({
  open,
  mapping,
  onClose,
  onSaved,
  // Optional: a File object the caller wants auto-loaded as the sample.
  // Used by the manual-upload drift flow — when a file is rejected with
  // 409 because columns changed, we pass that file in so the user sees
  // the new headers immediately instead of having to re-pick it.
  initialSample = null,
  // Called when the user saves a remap that started with an initialSample.
  // Health page uses this to auto-retry the upload with the fixed mapping.
  onSavedWithSample = null,
  // Optional pre-computed diff (missing/new headers) to surface in an
  // alert at the top of the dialog when we opened due to drift.
  columnDrift = null,
}) {
  const type = mapping?.integration_type;
  const mappingId = mapping?.id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Metadata (backend field list + labels) is fetched once per open.
  const [metadata, setMetadata] = useState(null);

  // The column-mapping UI state — mirrors what the wizard tracks.
  const [mappings, setMappings] = useState({});
  const [transforms, setTransforms] = useState({});
  const [headers, setHeaders] = useState([]);
  const [sampleValues, setSampleValues] = useState({});
  const [suggestions, setSuggestions] = useState(null);

  // Sample-file state (optional — used only when client wants to refresh
  // the header set because their CRM's columns changed).
  const [uploading, setUploading] = useState(false);
  const [sampleInfo, setSampleInfo] = useState(null);

  const reset = () => {
    setError(null);
    setMetadata(null);
    setMappings({});
    setTransforms({});
    setHeaders([]);
    setSampleValues({});
    setSuggestions(null);
    setUploading(false);
    setSampleInfo(null);
  };

  // Hydrate the dialog when it opens: pull metadata + the existing mapping
  // so we can pre-populate the column-mapping form with what's live today.
  useEffect(() => {
    if (!open || !type || !mappingId) return undefined;
    let cancelled = false;
    reset();
    setLoading(true);
    Promise.all([fetchMetadata(), fetchMapping(type, mappingId)])
      .then(([metaRes, mapRes]) => {
        if (cancelled) return;
        setMetadata(metaRes.data || {});
        const m = mapRes.data || {};
        const existing = m.column_mappings || {};
        setMappings(existing);
        // Extract transform rules back into the wizard's UI shape.
        const tp = m.transform_params || {};
        const ts = rulesFromTransformations(tp.transformations || []);
        setTransforms(ts);
        // Seed the headers list from the saved mapping so the table renders
        // even before the user uploads a fresh sample.
        setHeaders(Object.keys(existing));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || err?.message || 'Failed to load mapping details.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, type, mappingId]);

  // Drift-flow helper: after metadata+mapping loads, if the caller handed us
  // a sample file (because the upload 409'd with column drift), run it
  // through auto-match so the user sees the new headers immediately.
  useEffect(() => {
    if (!open) return;
    if (!initialSample) return;
    if (loading) return;
    if (sampleInfo) return; // already processed
    // Fire-and-forget; errors surface in the dialog's error alert.
    handleFileUpload(initialSample);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSample, loading]);

  const backendFields = metadata?.backend_fields || [];
  const fieldLabels = metadata?.field_labels || {};

  const handleFileUpload = useCallback(async (file) => {
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await autoMatchColumns(formData);
      const data = res.data || {};
      const detectedHeaders = Object.keys(data.suggestions || {}).concat(data.unmapped || []);

      // Merge strategy:
      //   - Keep existing mappings for headers that still exist in the new sample.
      //   - For brand-new headers, apply the auto-match suggestion.
      //   - Silently drop mappings for headers that no longer appear (they'd
      //     never match a real row anyway — user can re-add if they come back).
      setMappings((prev) => {
        const next = {};
        detectedHeaders.forEach((h) => {
          if (prev[h]) {
            next[h] = prev[h];
          } else if (data.suggestions?.[h]?.field) {
            next[h] = data.suggestions[h].field;
          }
        });
        return next;
      });
      setHeaders(detectedHeaders);
      setSuggestions(data);
      setSampleValues(data.sample_values || {});
      setSampleInfo({ name: file.name, size: file.size });
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || 'Failed to read sample file.'
      );
    } finally {
      setUploading(false);
    }
  }, []);

  const cleanMappings = useMemo(() => {
    const out = {};
    Object.entries(mappings || {}).forEach(([h, f]) => {
      if (f) out[h] = f;
    });
    return out;
  }, [mappings]);

  const validation = useMemo(() => {
    const mapped = new Set(Object.values(cleanMappings));
    // Transform rules also satisfy required fields — if the user has a
    // date_sold_date copy rule, the field is effectively mapped.
    Object.keys(transforms || {}).forEach((key) => {
      if (key.startsWith('date_') || key.startsWith('bool_')) {
        const field = key.split('_').slice(1).join('_');
        const rule = transforms[key];
        if (rule && rule.type) mapped.add(field);
      }
    });
    const required = ['prospect_id', 'created_date', 'net_amount'];
    const missing = required.filter((f) => !mapped.has(f));
    const hasContact = mapped.has('phone') || mapped.has('email');
    return { missing, hasContact };
  }, [cleanMappings, transforms]);

  const handleSave = async () => {
    if (!type || !mappingId) return;
    setSaving(true);
    setError(null);
    try {
      const transformations = buildTransformationsFromRules(transforms);
      const payload = {
        column_mappings: cleanMappings,
      };
      // Preserve non-transformation keys on transform_params by PATCHing a
      // merged shape. We've fetched the current value already, but the
      // server's PATCH will replace the full transform_params field. To be
      // safe, fetch again and merge before save.
      const current = await fetchMapping(type, mappingId);
      const currentTp = current.data?.transform_params || {};
      const nextTp = { ...currentTp };
      if (transformations.length) {
        nextTp.transformations = transformations;
      } else {
        delete nextTp.transformations;
      }
      payload.transform_params = nextTp;

      await updateMapping(type, mappingId, payload);
      onSaved?.();
      // Drift flow: hand the caller the sample that triggered the remap so
      // it can re-run the upload with the fixed mapping.
      if (initialSample && typeof onSavedWithSample === 'function') {
        onSavedWithSample(initialSample);
      }
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail
        || (typeof err?.response?.data === 'object' ? JSON.stringify(err.response.data) : null)
        || err?.message
        || 'Failed to save column mapping.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const canSave = !saving
    && !loading
    && validation.missing.length === 0
    && validation.hasContact
    && headers.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Remap columns
        <Typography variant="body2" color="text.secondary">
          {mapping?.client_name} — {type}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {columnDrift ? (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Columns in your file changed.</strong> We detected
                  {' '}{columnDrift.missing?.length || 0} missing column(s) and
                  {' '}{columnDrift.new_headers?.length || 0} new one(s).
                  Confirm the mapping below, then we'll retry your import automatically.
                </Typography>
                {columnDrift.missing?.length ? (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>No longer in the file:</strong>{' '}
                    {columnDrift.missing.slice(0, 6).join(', ')}
                    {columnDrift.missing.length > 6 ? `, +${columnDrift.missing.length - 6} more` : ''}
                  </Typography>
                ) : null}
                {columnDrift.new_headers?.length ? (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>New in the file:</strong>{' '}
                    {columnDrift.new_headers.slice(0, 6).join(', ')}
                    {columnDrift.new_headers.length > 6 ? `, +${columnDrift.new_headers.length - 6} more` : ''}
                  </Typography>
                ) : null}
              </Alert>
            ) : (
              <Alert severity="info">
                You can adjust how each column in your file maps to our fields below.
                If your CRM added or renamed columns, upload a fresh sample file
                and we'll detect them — your existing mappings are kept for any
                header name that hasn't changed.
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {sampleInfo && (
              <Alert severity="success" variant="outlined">
                Loaded headers from <strong>{sampleInfo.name}</strong>
                {sampleInfo.size ? ` (${Math.round(sampleInfo.size / 1024)} KB)` : ''}.
                {suggestions?.missing_required?.length ? (
                  <>
                    {' '}
                    Missing required: {suggestions.missing_required.join(', ')}.
                  </>
                ) : null}
              </Alert>
            )}

            {headers.length === 0 ? (
              <Alert severity="warning">
                No headers are stored on this mapping yet. Upload a sample file below to detect columns.
              </Alert>
            ) : null}

            <Divider />

            <StepColumnMapping
              mappings={mappings}
              onMappingsChange={setMappings}
              headers={headers}
              onFileUpload={handleFileUpload}
              backendFields={backendFields}
              fieldLabels={fieldLabels}
              uploading={uploading}
              suggestions={suggestions}
              transforms={transforms}
              onTransformsChange={setTransforms}
              sampleValues={sampleValues}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {saving ? 'Saving…' : 'Save column mapping'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
